import * as React from "react"

import { getPluginSlots } from "@/plugin/slots"
import { cn } from "@/lib/utils"

export type CardHeaderProps = React.ComponentProps<"div"> & {
  /**
   * When false, omit `CardTitle` / main heading. The header row ceases to take layout height:
   * actions are floated to the top-right of the card so body content aligns to the card top (no spare row).
   */
  showTitle?: boolean
}

export type CardDescriptionProps = React.ComponentProps<"div"> & {
  /**
   * When the slot carries primary/supporting body text (not a header subtitle), set true so it
   * still renders when the TalentCo client hides default card subtitles.
   */
  alwaysShow?: boolean
}

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "relative bg-card text-card-foreground flex flex-col gap-3 rounded-xl border p-[var(--sp-card)]",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, showTitle = true, ...props }: CardHeaderProps) {
  return (
    <div
      data-slot="card-header"
      data-action-only={showTitle ? undefined : ""}
      className={cn(
        showTitle
          ? "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4"
          : cn(
              "pointer-events-none absolute right-[var(--sp-card)] top-[var(--sp-card)] z-[1] flex w-auto max-w-full justify-end",
              "[.border-b]:pb-0",
              "[&_[data-slot=card-action]]:pointer-events-auto [&_[data-slot=card-action]]:!static [&_[data-slot=card-action]]:shrink-0"
            ),
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, alwaysShow, ...props }: CardDescriptionProps) {
  if (getPluginSlots().hideCardDescription && !alwaysShow) {
    return null
  }
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-0", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-0 [.border-t]:pt-[var(--sp-3)]", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
